import { useEffect, useMemo, useState } from 'react';
import { Formik } from 'formik';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { setModals } from '@/store/actions/commonActions';
import { MODAL_TYPES } from '@/components/ModalProvider';
import { useGetFundraisingStatusById } from '@/query/queries/fundraising/useGetFundraisingStatusById';
import { useGetFundraisingApplicationList } from '@/query/queries/fundraising/useGetFundraisingApplicationList';
import { useResetFundraising } from '@/query/queries/fundraising/useResetFundraising';
import { useSetFundraising } from '@/query/queries/fundraising/useSetFundraising';
import { useGetTariffPlans } from '@/query/queries/fundraising/useGetTariffPlans';
import { useSaveFpgAssets } from '@/query/queries/fundraising/useSaveFpgAssets';
import { getDaysLeft } from '@/helpers/date';
import { FPG_ASSETS_STATUS, ROLES } from '@/helpers/constants';
import { FR_APPLICATION_STATUSES, FR_STEPS } from '@/helpers/fundraising';
import { useApplicationStatusByFundraisingId } from '@/hooks/shared/useApplicationStatusByFundraisingId';
import { BackButton } from '@/components/BackButton';
import { FundraisingStatus } from '../FundraisingStatus';
import { FundraisingStatusApplication } from '../FundraisingStatus/application';
import { StepPlanSelect } from '../StepPlanSelect';
import { StepFPGSelect } from '../StepFPGSelect';
import { StepFlexibilitySelect } from '../StepFlexibilitySelect/';
import { LoanTermStatus } from '../LoanTerms';
import { FoundersConfirmation } from '../FoundersConfirmation';
import { FundraisingMessages } from '../FundraisingMessages';
import { ApplicationMessages } from '../ApplicationMessages';
import { FOUNDER_VALIDATION_SCHEMA } from '../../schema';
import IconEdit from '../../assets/icon_edit.svg';
import r from '@/kit/RoundButton/button.module.scss';
import s from './index.module.scss';

const STEPS = {
  tariff_select: 'tariff_select',
  fpg_select: 'fpg_select',
  flexibility_select: 'flexibility_select',
};

export const FounderView = ({ disabled, fundraisingId, onBack }) => {
  const [step, setStep] = useState(null);
  const { data: status } = useGetFundraisingStatusById(fundraisingId);
  const { data: plans } = useGetTariffPlans();
  const { application } = useApplicationStatusByFundraisingId(fundraisingId);
  const { mutateAsync: setFundraising } = useSetFundraising();
  const dispatch = useDispatch();
  const { mutateAsync: resetFundraising } = useResetFundraising();
  const { mutateAsync: saveFpgAssets } = useSaveFpgAssets();
  const { data: applicationList } = useGetFundraisingApplicationList();
  const { t } = useTranslation('fundraising');

  const location = useLocation();
  const navigate = useNavigate();

  const currentRound = applicationList?.[0]?.fundraising_id === fundraisingId;

  useEffect(() => {
    if (location.state?.step) {
      setStep(location.state?.step);
      navigate('.', { replace: true, state: null });
    }
  }, [location.state?.step]);

  const manualTariffSet = useMemo(
    () => !plans?.find(plan => plan.id === status?.tariff_plan.id),
    [plans, status],
  );

  const { freeFpgOption } = useMemo(() => {
    return { freeFpgOption: status?.available_fpg_options.find(fpg => fpg.amount === 0) };
  }, [status]);

  if (!status) return null;

  const onSubmit = async (values, formik) => {
    const payload = {
      new_funds: values.new_funds,
      has_accepted_terms: values.has_accepted_terms,
      fpg_option: values.fpg?.id,
      flexibility_option: values.flexibility_option?.id,
      tariff_plan: values.tariff_plan?.id,
    };

    if (
      freeFpgOption?.id === values.fpg?.id &&
      status?.fpg_assets_status === FPG_ASSETS_STATUS.new &&
      values.fpg_assets.length
    ) {
      await saveFpgAssets({ file_ids: values.fpg_assets.map(f => f.id) });
    }

    const res = await setFundraising(payload);
    if (res?.step === FR_STEPS.fundraising_summary) {
      setStep(null);
    }
    formik.setSubmitting(false);
  };

  const onReset = async () => {
    dispatch(
      setModals({
        type: MODAL_TYPES.confirm_modal,
        onConfirm: async () => {
          await resetFundraising();
          dispatch(setModals([]));
        },
        acceptLabel: t('fundraising:shared.reset'),
        cancelLabel: t('fundraising:shared.keep_the_goal'),
        title: t('fundraising:shared.changed_your_mind'),
        desc: t('fundraising:shared.opt_out_or_change_settings'),
      }),
    );
  };

  const canReset = Boolean(status?.step === FR_STEPS.fundraising_summary && !status?.is_active);
  const daysLeft = getDaysLeft(status?.start_date);

  return (
    <Formik
      enableReinitialize
      onSubmit={onSubmit}
      validationSchema={FOUNDER_VALIDATION_SCHEMA(t, {
        maxNewFunds: status.max_new_funds || 0,
        freeFpgOption,
        fpgAssetsRequired: !!freeFpgOption && status?.fpg_assets_status === FPG_ASSETS_STATUS.new,
      })}
      initialValues={{
        new_funds: status?.new_funds || 0,
        tariff_plan: status?.tariff_plan || null,
        fpg: status?.fpg_option
          ? status?.fpg_option
          : status?.available_fpg_options.find(option => option.is_default) || null,
        flexibility_option: status?.flexibility_option
          ? status?.flexibility_option
          : status?.available_flexibility_options.find(option => option.is_default) || null,
        has_accepted_terms: false,
        fpg_assets: [],
      }}
    >
      {({ values, handleSubmit }) => (
        <form className={s.form} onSubmit={handleSubmit}>
          <div className="container-inner" style={{ margin: '50px 0' }}>
            <div className={s.wrapper}>
              <BackButton onClick={onBack} />
              <h1 className="f-hero-h2">{t('page.title')}</h1>
              <FundraisingMessages />
              <ApplicationMessages fundraisingId={fundraisingId} role={ROLES.founder} />
              <div className={s.header}>
                <div className={s.header__left}>
                  <h3 className="f-h3 f-500">
                    {status?.is_active
                      ? t('founder_view.fundraising_goal_for_the_round')
                      : t('founder_view.fundraising_goal_for_the_next_round')}
                  </h3>
                  {status?.step === FR_STEPS.set_goal && (
                    <div className="f-label" style={{ color: 'var(--Grey-500, #908A94)' }}>
                      {disabled
                        ? t('founder_view.contact_account_manager')
                        : t('founder_view.set_goal_before_each_round')}
                    </div>
                  )}
                </div>
                {canReset ? (
                  <button
                    type="button"
                    className={classnames(r.black_small, s.btn_reset)}
                    onClick={onReset}
                  >
                    {t('fundraising:shared.reset_or_edit_the_goal')}{' '}
                    <img src={IconEdit} alt="reset" width={24} height={24} />
                  </button>
                ) : (
                  status?.step === FR_STEPS.set_goal &&
                  currentRound && (
                    <button
                      type="button"
                      disabled={disabled}
                      className={classnames(r.yellow_base)}
                      onClick={() =>
                        manualTariffSet ? setStep(STEPS.fpg_select) : setStep(STEPS.tariff_select)
                      }
                    >
                      {t('fundraising:shared.proceed_with_this_goal')}
                    </button>
                  )
                )}
              </div>

              {application && application.status === FR_APPLICATION_STATUSES.APPROVED ? (
                <FundraisingStatus
                  fundraisingId={fundraisingId}
                  newFunds={values.new_funds}
                  disabled={disabled}
                  hideTitle
                  mainFounder
                />
              ) : (
                <FundraisingStatusApplication fundraisingId={fundraisingId} hideTitle />
              )}

              {status.step === FR_STEPS.fundraising_summary && (
                <>
                  <LoanTermStatus status={status} />
                  <FoundersConfirmation />
                </>
              )}
              {status?.step === FR_STEPS.set_goal &&
                !status.is_active &&
                daysLeft &&
                currentRound && (
                  <div className={s.actions}>
                    <div className="f-body">
                      {t('founder_view.till_the_next_round', { daysLeft })}
                    </div>
                  </div>
                )}
            </div>
          </div>

          {step === STEPS.tariff_select && (
            <StepPlanSelect
              selected={values.tariff_plan}
              manualTariffSet={manualTariffSet}
              onBack={() => setStep(null)}
              onNext={() => setStep(STEPS.fpg_select)}
            />
          )}

          {step === STEPS.fpg_select && (
            <StepFPGSelect
              disabled={disabled}
              manualTariffSet={manualTariffSet}
              freeFpgOption={freeFpgOption}
              onBack={() => (manualTariffSet ? setStep(null) : setStep(STEPS.tariff_select))}
              onNext={() => setStep(STEPS.flexibility_select)}
            />
          )}

          {step === STEPS.flexibility_select && (
            <StepFlexibilitySelect
              disabled={disabled}
              freeFpgOption={freeFpgOption}
              onBack={() => setStep(STEPS.fpg_select)}
              onNext={() => setStep(STEPS.flexibility_select)}
            />
          )}
        </form>
      )}
    </Formik>
  );
};
